import React from 'react';
import { useSelector } from 'react-redux';

import { getThemePreference, type DsTheme } from 'src/module/authentication/theme.selector';
import { hasChemelexUiAlphaFeature } from 'src/module/authentication/chemelexUi.selector';

/**
 * Which theme the DS surfaces below are drawing in.
 *
 * Context rather than a selector at each surface, so DsRoot stays renderable
 * without a redux Provider -- the design-system screen and the component tests
 * mount it bare. `DsThemeProvider` below is the one place that reads the store.
 *
 * To *read* the theme, use `useDsTheme`. This context exists to apply the
 * class, and nothing below needs to branch on it: the components are styled
 * with tokens that `.ds-dark` redefines.
 */

/** The dark selector Tailwind compiles from `darkMode: 'class'` + the prefix. */
export const DS_DARK_CLASS = 'ds-dark';

export const DARK_QUERY = '(prefers-color-scheme: dark)';

const DsDarkContext = React.createContext(false);

/** `undefined` rather than '' so `cn` drops it. */
export function useDsThemeClass (): string | undefined {
  return React.useContext(DsDarkContext) ? DS_DARK_CLASS : undefined;
}

const query = window.matchMedia ? window.matchMedia(DARK_QUERY) : null;

const subscribeToSystemTheme = (onChange: () => void) => {
  if (!query?.addEventListener) {
    return () => {};
  }

  query.addEventListener('change', onChange);
  return () => query.removeEventListener('change', onChange);
};

const getSystemPrefersDark = () => query?.matches ?? false;

/*
 * Subscribed rather than read once: the OS theme can flip while the tab is open
 * and the page follows.
 */
export function useSystemPrefersDark (): boolean {
  return React.useSyncExternalStore(subscribeToSystemTheme, getSystemPrefersDark);
}


export function useDsTheme (): DsTheme {
  const preference = useSelector(getThemePreference);
  const canUseDarkMode = useSelector(hasChemelexUiAlphaFeature);
  const systemPrefersDark = useSystemPrefersDark();

  if (!canUseDarkMode) {
    return 'light';
  }
  if (preference !== 'system') {
    return preference;
  }
  return systemPrefersDark ? 'dark' : 'light';
}

/** Test seam: supplies a theme without a store. Production uses DsThemeProvider. */
export function DsDarkProvider (
  { isDark, children }: { isDark: boolean; children?: React.ReactNode }
) {
  return <DsDarkContext.Provider value={isDark}>{children}</DsDarkContext.Provider>;
}

/** Supplies the resolved theme to every DS surface below. Mounted once, at the root. */
export function DsThemeProvider ({ children }: { children?: React.ReactNode }) {
  const theme = useDsTheme();

  return <DsDarkProvider isDark={theme === 'dark'}>{children}</DsDarkProvider>;
}
